import Grid from "@vapor/react-material/Grid";
import Stack from "@vapor/react-material/Stack";
import Typography from "@vapor/react-extended/ExtendedTypography";
import { Divider, Autocomplete, Button, Box } from "@vapor/react-material";
import Header from "../components/Header";
import NoFolders from "../components/NoFolders";
import SearchInput from "../core/SearchInput";
import { useEffect, useMemo, useState } from "react";
import { AutocompleteRenderInputParams, TextField } from "@mui/material";
import FolderCard from "../components/FolderCard";
import React from "react";
import DeleteAlert from "../components/alerts/DeleteAlert";
import { useGetAllFolders } from "../hooks/useGetAllFolders";
import { useTranslation } from "@onefront/react-sdk";
import { Folder } from "../types/FolderInteface";
import ShareFolderModal from "../components/dashboard/ShareFolderModal";
import DocumentPreviewModal from "../components/dashboard/DocumentPreviewModal";
import { useClientInformationStore } from "../../store/useClientInformationStore";
import { WorkspaceSelector } from "@drift/oneplatfront-components";
import NoAccessErrorPage from "../../common-components/NoAccessError";

const FoldersPage = () => {
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(8);
  const [allFolders, setAllFolders] = useState<Folder[]>([]);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openShareModal, setOpenShareModal] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [openPreviewModal, setOpenPreviewModal] = useState(false);
  const [filterValue, setFilterValue] = useState("NEWEST");
  const filteringOptions = [
    "NEWEST",
    "OLDEST",
    "MOST_RECENTLY_UPDATED",
    "LEAST_RECENTLY_UPDATED",
    "ASCENDING",
    "DESCENDING",
  ];

  const { workspaceId, selectedWorkspace: ws } = useClientInformationStore();
  const { t } = useTranslation("DOCS");

  const workspaceDescription = ws
    ? `${
        [...ws?.companies, ...ws?.accountants].find((x) => x.id === workspaceId)
          ?.description
      }`
    : "";

  const {
    data: folders,
    loading,
    error,
    fetch,
    hasLoaded,
  } = useGetAllFolders(workspaceId, page, size, filterValue);

  useEffect(() => {
    if (hasLoaded && folders?.data && Array.isArray(folders.data)) {
      setAllFolders((prev: Folder[]) => [...prev, ...folders.data]);
    }
  }, [folders]);

  const searchFolders = () => {
    return allFolders.filter((folder) => folder.text.startsWith(searchValue));
  };

  const filteredFolders = useMemo(
    () => (!!searchValue ? searchFolders() : allFolders),
    [searchValue, allFolders]
  );

  return (
    <>
      <Stack
        direction="column"
        paddingTop="20px"
        marginLeft="16px"
        spacing={2}
        marginRight="16px"
      >
        <Grid container alignItems="center">
          <Grid item xs={4} />
          <Grid item xs={4}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Cairo",
                  fontWeight: "600",
                  textDecoration: "none",
                  textTransform: "none",
                  color: "#0077ad",
                  padding: "0px",
                  lineHeight: "1.5",
                  fontSize: "20px",
                }}
              >
                {t("folder.folder")}
              </Typography>
              <Typography
                sx={{
                  margin: "0px",
                  fontFamily: "Cairo",
                  color: "rgb(98, 121, 132)",
                  fontWeight: "400",
                  fontSize: "12px",
                  padding: "0px",
                  lineHeight: "1",
                }}
              >
                {!workspaceDescription ? workspaceDescription : " "}
              </Typography>
            </div>
          </Grid>
          <Grid item xs={4} display="flex" justifyContent="right">
            <WorkspaceSelector />
          </Grid>
        </Grid>
        <Divider />
        {ws?.companies?.length || ws?.accountants?.length ? (
          <>
            <Header />
            <hr />
            {filteredFolders.length === 0 && !searchValue && !loading ? (
              <NoFolders />
            ) : (
              <div style={{ width: "inherit" }}>
                <Box
                  sx={{
                    direction: "row",
                    width: "inherit",
                    display: "flex",
                    alignContent: "space-between",
                    justifyContent: "space-between",
                  }}
                >
                  <SearchInput
                    setSearchValue={setSearchValue}
                    onSearch={searchFolders}
                    width="310px"
                  />
                  <Autocomplete
                    disablePortal
                    fullWidth={false}
                    value={filterValue}
                    onChange={(_, value: any) => {
                      setSize(8);
                      setPage(1);
                      setAllFolders([]);
                      setFilterValue(value);
                    }}
                    id="grouped-demo"
                    options={filteringOptions}
                    renderInput={(params) => (
                      <TextField
                        placeholder={t("folder.chooseOption")}
                        {...params}
                      />
                    )}
                  />
                </Box>
                <Grid
                  container
                  marginTop="20px"
                  rowSpacing={2}
                  columns={{ xs: 3, sm: 3, md: 3, lg: 4, xl: 5 }}
                  columnSpacing={{ xs: 4, sm: 4, md: 3, lg: 2 }}
                >
                  {filteredFolders.map((folder: any, index) => (
                    <Grid item xs={1} key={index}>
                      <FolderCard
                        key={folder.id}
                        folder={folder}
                        setOpenPreviewModal={setOpenPreviewModal}
                        setOpenDeleteModal={(value: boolean) =>
                          setOpenDeleteModal(value)
                        }
                        setOpenShareModal={setOpenShareModal}
                        setSelectedFolderId={setSelectedFolderId}
                        setSelectedDocumentId={setSelectedDocumentId}
                      />
                    </Grid>
                  ))}
                </Grid>
              </div>
            )}
          </>
        ) : (
          <NoAccessErrorPage />
        )}
      </Stack>
      {(filteredFolders.length > 0 && ws?.companies?.length) ||
        (ws?.accountants?.length && (
          <div style={{ textAlign: "center" }}>
            <Button
              variant="outlined"
              disabled={folders?.count === 0}
              onClick={() => setPage((prev) => prev + 1)}
              sx={{
                color: "rgb(0, 144, 209)",
                margin: "20px",
                padding: "20px",
              }}
            >
              {t("folder.seeMore")}
            </Button>
          </div>
        ))}
      {openDeleteModal && (
        <DeleteAlert
          open={openDeleteModal}
          setOpen={setOpenDeleteModal}
          id={selectedFolderId}
          onDelete={() => {
            setSize(8);
            setPage(1);
            setAllFolders([]);
            fetch();
          }}
        />
      )}
      {openShareModal && (
        <ShareFolderModal open={openShareModal} setOpen={setOpenShareModal} />
      )}
      {openPreviewModal && (
        <DocumentPreviewModal
          id={selectedDocumentId}
          open={openPreviewModal}
          setOpen={setOpenPreviewModal}
        />
      )}
    </>
  );
};
export default FoldersPage;
